import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { bookingsAPI } from '../utils/api';

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#2C2C2C',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': {
        color: '#6B6B6B',
      },
      padding: '12px',
    },
    invalid: {
      color: '#C62828',
      iconColor: '#C62828',
    },
  },
};

function CardSetupForm({ booking, clientSecret, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Confirm the SetupIntent with the card element
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: booking.customer_name,
            email: booking.customer_email,
          },
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      // Save the card to the booking
      await bookingsAPI.saveCard(booking.id, {
        setup_intent_id: setupIntent.id,
        payment_method_id: setupIntent.payment_method,
      });

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save card. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>Complete Your Booking Request</h2>

      {/* Booking Summary */}
      <div style={{
        background: '#FAF8F5',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1.5rem'
      }}>
        <h4 style={{ margin: '0 0 0.5rem 0' }}>Booking Summary</h4>
        <p style={{ margin: '0.25rem 0' }}><strong>Service:</strong> {booking.service_name}</p>
        <p style={{ margin: '0.25rem 0' }}><strong>Date:</strong> {booking.booking_date}</p>
        <p style={{ margin: '0.25rem 0' }}><strong>Time:</strong> {booking.start_time}</p>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.25rem', color: '#2C2C2C' }}>
          <strong>Total: £{booking.service_price || booking.price}</strong>
        </p>
      </div>

      {/* Approval Notice */}
      <div style={{
        background: '#F0F7F1',
        border: '1px solid #A8C9AD',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#4A7C59' }}>
          <strong>📋 Booking Approval:</strong> Your request will be reviewed and you'll receive a confirmation email once approved.
        </p>
      </div>

      {/* No-show Policy */}
      <div style={{
        background: '#FFF8E1',
        border: '1px solid #E09F3E',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1.5rem'
      }}>
        <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
          <strong>💳 Card Policy:</strong> Your card will <strong>not</strong> be charged today.
          We require card details to protect against no-shows.
        </p>
        <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#8B6914' }}>
          <strong>Cancellation fees:</strong> 48h+ free | 24-48h 50% | &lt;24h 100%
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem' }}>
          <a href="/policies" target="_blank" rel="noopener noreferrer" style={{ color: '#8B6914' }}>
            View full cancellation & privacy policies
          </a>
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Card Details
          </label>
          <div style={{
            border: '1px solid #E8E4E0',
            borderRadius: '8px',
            padding: '12px',
            background: '#fff'
          }}>
            <CardElement options={cardElementOptions} />
          </div>
        </div>

        {error && (
          <div style={{
            background: '#FEE9E9',
            color: '#C62828',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!stripe || processing}
            style={{ flex: 1 }}
          >
            {processing ? 'Submitting...' : 'Submit Booking Request'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={processing}
          >
            Cancel
          </button>
        </div>
      </form>

      <p style={{
        textAlign: 'center',
        marginTop: '1rem',
        fontSize: '0.8rem',
        color: '#6B6B6B'
      }}>
        Your payment information is securely processed by Stripe.
      </p>
    </div>
  );
}

export default CardSetupForm;
